import { z } from 'zod';
import { SUPPORTED_LOCALES } from '../constants/app.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { isoDateSchema, moneySchema, quantitySchema } from './common.js';
import { postalDocumentAddressSchema } from './document.js';
import { dmsCategorySchema } from './dms.js';
import { documentItemTypeSchema, documentStatusSchema, paymentStatusSchema } from './enums.js';
import { paginationMetaSchema } from './pagination.js';
import { strongPasswordSchema } from './security.js';
export const PORTAL_ROLES = ['ADMIN', 'PURCHASING', 'READ_ONLY'];
export const portalRoleSchema = z.enum(PORTAL_ROLES);
export const PORTAL_PERMISSIONS = {
    VIEW_ACCOUNT: 'portal:view_account',
    MANAGE_USERS: 'portal:manage_users',
    VIEW_DOCUMENTS: 'portal:view_documents',
    PLACE_ORDERS: 'portal:place_orders',
};
export const PORTAL_ROLE_PERMISSIONS = {
    ADMIN: Object.values(PORTAL_PERMISSIONS),
    PURCHASING: [
        PORTAL_PERMISSIONS.VIEW_ACCOUNT,
        PORTAL_PERMISSIONS.VIEW_DOCUMENTS,
        PORTAL_PERMISSIONS.PLACE_ORDERS,
    ],
    READ_ONLY: [PORTAL_PERMISSIONS.VIEW_ACCOUNT, PORTAL_PERMISSIONS.VIEW_DOCUMENTS],
};
export const portalUserSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    contactId: idSchema,
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    displayName: z.string(),
    locale: z.enum(SUPPORTED_LOCALES),
    role: portalRoleSchema,
    permissions: z.array(z.string()),
    tenantId: idSchema,
    tenantSlug: z.string().min(1),
    tenantName: z.string(),
    customerName: z.string(),
    isActive: z.boolean(),
    emailTwoFactorRequired: z.boolean(),
    emailVerifiedAt: isoDateTimeSchema,
    lastLoginAt: isoDateTimeSchema.nullable(),
});
export const portalSessionResponseSchema = z.object({
    user: portalUserSchema,
    csrfToken: z.string().min(32),
    expiresInSeconds: z.number().int().positive(),
});
export const portalLoginSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(60),
    email: z.string().trim().toLowerCase().email().max(200),
    password: z.string().min(8).max(200),
});
export const portalLoginOutcomeSchema = z.union([
    portalSessionResponseSchema.extend({ twoFactorRequired: z.literal(false) }),
    z.object({
        twoFactorRequired: z.literal(true),
        challengeToken: z.string().min(32),
        maskedEmail: z.string(),
        expiresInSeconds: z.number().int().positive(),
    }),
]);
export const portalTwoFactorVerifySchema = z.object({
    challengeToken: z.string().min(32).max(500),
    code: z.string().regex(/^\d{6}$/u),
});
export const portalInvitationCreateSchema = z.object({
    contactId: idSchema,
    role: portalRoleSchema,
    locale: z.enum(SUPPORTED_LOCALES).optional(),
});
export const portalInvitationAcceptSchema = z
    .object({
    token: z.string().min(32).max(500),
    password: strongPasswordSchema,
    passwordConfirmation: z.string().min(1).max(200),
})
    .refine((value) => value.password === value.passwordConfirmation, {
    path: ['passwordConfirmation'],
    message: 'Die Passwörter stimmen nicht überein.',
});
export const portalInvitationPreviewSchema = z.object({
    tenantSlug: z.string(),
    customerName: z.string(),
    contactName: z.string(),
    maskedEmail: z.string(),
    role: portalRoleSchema,
    locale: z.string(),
    expiresAt: isoDateTimeSchema,
});
export const portalInvitationSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    contactId: idSchema,
    contactName: z.string(),
    email: z.string().email(),
    role: portalRoleSchema,
    locale: z.string(),
    status: z.enum(['PENDING', 'EXPIRED', 'ACCEPTED', 'REVOKED']),
    expiresAt: isoDateTimeSchema,
    usedAt: isoDateTimeSchema.nullable(),
    revokedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const portalManagedUserSchema = portalUserSchema
    .pick({
    id: true,
    customerId: true,
    contactId: true,
    email: true,
    firstName: true,
    lastName: true,
    displayName: true,
    locale: true,
    role: true,
    isActive: true,
    emailTwoFactorRequired: true,
    emailVerifiedAt: true,
    lastLoginAt: true,
})
    .extend({ activeSessionCount: z.number().int().min(0), lockedAt: isoDateTimeSchema.nullable() });
export const portalAccessOverviewSchema = z.object({
    users: z.array(portalManagedUserSchema),
    invitations: z.array(portalInvitationSchema),
});
export const portalContactOptionSchema = z.object({
    id: idSchema,
    name: z.string(),
    email: z.string().email(),
    locale: z.string(),
    hasPortalAccess: z.boolean(),
    hasPendingInvitation: z.boolean(),
});
export const portalUserUpdateSchema = z
    .object({
    role: portalRoleSchema.optional(),
    isActive: z.boolean().optional(),
    lockReason: z.string().trim().min(3).max(500).optional(),
    emailTwoFactorRequired: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'Mindestens eine Änderung ist erforderlich.');
export const portalProfileUpdateSchema = z.object({
    locale: z.enum(SUPPORTED_LOCALES),
});
export const portalPasswordResetRequestSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(60),
    email: z.string().trim().toLowerCase().email().max(200),
});
export const portalPasswordResetCompleteSchema = z
    .object({
    token: z.string().min(32).max(500),
    password: strongPasswordSchema,
    passwordConfirmation: z.string().min(1).max(200),
})
    .refine((value) => value.password === value.passwordConfirmation, {
    path: ['passwordConfirmation'],
    message: 'Die Passwörter stimmen nicht überein.',
});
export const portalSessionEntrySchema = z.object({
    id: idSchema,
    current: z.boolean(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    lastSeenAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
});
export const internalPortalAccessOverviewSchema = z.object({
    users: z.array(portalManagedUserSchema.extend({ activeSessions: z.array(portalSessionEntrySchema) })),
    invitations: z.array(portalInvitationSchema),
});
export const PORTAL_ACTIVITY_EVENTS = [
    'LOGIN_SUCCESS',
    'LOGIN_FAILED',
    'LOGOUT',
    'INVITATION_CREATED',
    'INVITATION_RESENT',
    'INVITATION_REVOKED',
    'INVITATION_ACCEPTED',
    'USER_LOCKED',
    'USER_UNLOCKED',
    'SESSION_REVOKED',
    'PASSWORD_RESET_REQUESTED',
    'PASSWORD_RESET_COMPLETED',
    'TWO_FACTOR_CHALLENGE',
    'TWO_FACTOR_VERIFIED',
    'TWO_FACTOR_FAILED',
    'PROFILE_CHANGED',
    'DOCUMENT_OPENED',
    'DOCUMENT_DOWNLOADED',
    'QUOTE_VIEWED',
    'ORDER_CREATED',
    'ORDER_SUBMITTED',
    'DELIVERY_ADDRESS_CHANGED',
    'ACTION_FAILED',
    'ERROR_DISPLAYED',
];
export const PORTAL_ERROR_KINDS = [
    'price',
    'cart',
    'stock',
    'session',
    'permission',
    'validation',
    'mode',
    'network',
    'server',
    'rate',
    'missing',
    'credentials',
    'generic',
];
export const portalErrorKindSchema = z.enum(PORTAL_ERROR_KINDS);
export const PORTAL_REPORT_LIMITS = {
    queue: 40,
    batch: 10,
    maxAgeMs: 86_400_000,
    retryMs: 30_000,
    attempts: 8,
};
export const portalDiagnosticSchema = z.object({
    source: z.enum(['SERVER', 'BROWSER']),
    outcome: z.enum(['FAILED', 'DISPLAYED', 'SUCCEEDED']),
    action: z.string().max(160),
    page: z.string().max(160),
    attemptId: z.string().uuid().nullable(),
    correlationId: z.string().max(100).nullable(),
    requestId: z.string().max(100).nullable(),
    trackingId: z.string().max(100).nullable(),
    code: z.string().max(100),
    messageKey: portalErrorKindSchema.nullable(),
    locale: z
        .enum(SUPPORTED_LOCALES)
        .nullable(),
    displayedMessage: z.string().max(1500).nullable(),
    parameters: z.object({ count: z.number().int().min(0).max(1000).optional() }).strict(),
    clientOccurredAt: isoDateTimeSchema.nullable(),
});
export const portalErrorReportSchema = z
    .object({
    eventId: z.string().uuid(),
    actorId: idSchema.nullable(),
    attemptId: z.string().uuid(),
    correlationId: z.string().uuid().nullable(),
    requestId: z
        .string()
        .max(100)
        .regex(/^[\w:-]+$/u)
        .nullable(),
    trackingId: z
        .string()
        .max(100)
        .regex(/^[\w:-]+$/u)
        .nullable(),
    code: z
        .string()
        .max(100)
        .regex(/^[A-Z0-9_]+$/u),
    page: z
        .string()
        .max(160)
        .regex(/^\/portal(?:\/[a-zA-Z0-9_-]+)*$/u),
    action: z
        .string()
        .max(160)
        .regex(/^[a-zA-Z0-9_:/ -]+$/u),
    targetId: idSchema.nullable(),
    messageKey: portalErrorKindSchema,
    locale: z.enum(SUPPORTED_LOCALES),
    parameters: z.object({ count: z.number().int().min(0).max(1000).optional() }).strict(),
    occurredAt: isoDateTimeSchema,
})
    .strict();
export const portalErrorBatchSchema = z
    .object({ events: z.array(portalErrorReportSchema).min(1).max(PORTAL_REPORT_LIMITS.batch) })
    .strict();
export const portalActivityEventSchema = z.enum(PORTAL_ACTIVITY_EVENTS);
export const portalActivityEntrySchema = z.object({
    id: idSchema,
    event: portalActivityEventSchema,
    actorName: z.string().nullable(),
    actorType: z.enum(['PORTAL_USER', 'INTERNAL_USER', 'SYSTEM']),
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
    targetLabel: z.string().nullable(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    occurredAt: isoDateTimeSchema,
    diagnostic: portalDiagnosticSchema.nullable(),
});
export const portalActivityQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const portalActivityResponseSchema = z.object({
    data: z.array(portalActivityEntrySchema),
    meta: z.object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().min(0),
        totalPages: z.number().int().min(0),
    }),
});
export const portalSettingsSchema = z.object({
    emailTwoFactorRequired: z.boolean(),
    invitationValidityHours: z.number().int().min(1).max(720),
    passwordResetValidityMinutes: z.number().int().min(5).max(1440),
    activityRetentionDays: z.number().int().min(30).max(3650),
    stockVisibility: z.enum(['EXACT', 'STATUS']),
    portalOrdersRequireReview: z.boolean(),
    allowBackorders: z.boolean(),
    allowPartialDelivery: z.boolean(),
    updatedAt: isoDateTimeSchema,
});
export const portalSettingsUpdateSchema = portalSettingsSchema
    .omit({ updatedAt: true })
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'Mindestens eine Änderung ist erforderlich.');
export const PORTAL_DOCUMENT_TYPES = [
    'QUOTE',
    'ORDER',
    'DELIVERY_NOTE',
    'INVOICE',
    'CREDIT_NOTE',
];
export const portalDocumentTypeSchema = z.enum(PORTAL_DOCUMENT_TYPES);
export const portalDocumentListQuerySchema = z
    .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().min(1).max(120).optional(),
    type: portalDocumentTypeSchema.optional(),
    status: documentStatusSchema.optional(),
    paymentStatus: paymentStatusSchema.optional(),
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
})
    .refine((value) => value.dateFrom === undefined || value.dateTo === undefined || value.dateFrom <= value.dateTo, { path: ['dateTo'], message: 'Das Enddatum darf nicht vor dem Startdatum liegen.' });
export const portalFileSchema = z.object({
    id: idSchema,
    title: z.string().nullable(),
    originalName: z.string(),
    mimeType: z.string(),
    fileSize: z.number().int().positive(),
    category: dmsCategorySchema,
    sourceType: z.enum(['CUSTOMER', 'DOCUMENT', 'DUNNING']),
    sourceId: idSchema,
    createdAt: isoDateTimeSchema,
});
export const portalFileListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().min(1).max(120).optional(),
    category: dmsCategorySchema.optional(),
});
export const portalFileListResponseSchema = z.object({
    data: z.array(portalFileSchema),
    meta: paginationMetaSchema,
});
export const portalDocumentSummarySchema = z.object({
    id: idSchema,
    type: portalDocumentTypeSchema,
    number: z.string(),
    status: documentStatusSchema,
    paymentStatus: paymentStatusSchema,
    documentDate: isoDateSchema,
    dueDate: isoDateSchema.nullable(),
    deliveryDate: isoDateSchema.nullable(),
    currency: z.string().length(3),
    netTotal: moneySchema,
    taxTotal: moneySchema,
    grossTotal: moneySchema,
    paidAmount: moneySchema,
    openAmount: moneySchema,
    itemCount: z.number().int().min(0),
    updatedAt: isoDateTimeSchema,
});
export const portalDocumentListResponseSchema = z.object({
    data: z.array(portalDocumentSummarySchema),
    meta: paginationMetaSchema,
});
export const portalDocumentItemSchema = z.object({
    id: idSchema,
    productId: idSchema.nullable(),
    packagingId: idSchema.nullable(),
    position: z.number().int().positive(),
    itemType: documentItemTypeSchema,
    articleNumber: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    quantity: quantitySchema,
    unitCode: z.string(),
    unitPrice: moneySchema,
    discountPct: z.string(),
    discountAmount: moneySchema,
    taxRatePct: z.string(),
    netAmount: moneySchema,
    taxAmount: moneySchema,
    grossAmount: moneySchema,
});
export const portalDocumentFlowEntrySchema = z.object({
    id: idSchema,
    type: portalDocumentTypeSchema,
    number: z.string(),
    status: documentStatusSchema,
    documentDate: isoDateSchema,
});
/**
 * Bereits serverseitig aufgelöste Portalansicht des Kundendatenkopfs. Rohwerte,
 * technische Feldschlüssel, Formeln und interne Sichtbarkeitsregeln sind absichtlich
 * kein Bestandteil dieses Transporttyps.
 */
export const portalCustomerHeaderFieldSchema = z.object({
    label: z.string().max(120),
    value: z.string().max(10_000),
    showLabel: z.boolean(),
    columnSpan: z.number().int().min(1).max(4),
});
export const portalCustomerHeaderSectionSchema = z.object({
    label: z.string().max(120),
    columns: z.number().int().min(1).max(4),
    density: z.enum(['COMPACT', 'DETAILED']),
    fields: z.array(portalCustomerHeaderFieldSchema).max(500),
});
export const portalDocumentDetailSchema = portalDocumentSummarySchema.extend({
    language: z.string(),
    reference: z.string().nullable(),
    paymentMethod: z.string(),
    paymentTermsDays: z.number().int().min(0),
    cashDiscountPct: z.string(),
    cashDiscountDays: z.number().int().min(0),
    deliveryTerms: z.string().nullable(),
    headerText: z.string().nullable(),
    footerText: z.string().nullable(),
    billingAddress: postalDocumentAddressSchema,
    shippingAddress: postalDocumentAddressSchema.nullable(),
    items: z.array(portalDocumentItemSchema),
    sourceDocument: portalDocumentFlowEntrySchema.nullable(),
    followingDocuments: z.array(portalDocumentFlowEntrySchema),
    attachments: z.array(portalFileSchema),
    customerHeader: z.array(portalCustomerHeaderSectionSchema).max(50),
});
export const portalDunningSummarySchema = z.object({
    id: idSchema,
    noticeNumber: z.string(),
    status: z.enum(['APPROVED', 'GENERATED', 'SENT', 'COLLECTION']),
    level: z.number().int().positive(),
    language: z.string(),
    currency: z.string().length(3),
    principalTotal: moneySchema,
    feeTotal: moneySchema,
    interestTotal: moneySchema,
    totalDue: moneySchema,
    generatedAt: isoDateTimeSchema.nullable(),
    sentAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    pdfAvailable: z.boolean(),
});
export const portalDunningItemSchema = z.object({
    id: idSchema,
    documentId: idSchema,
    documentNumber: z.string(),
    documentDate: isoDateSchema,
    dueDate: isoDateSchema,
    grossAmount: moneySchema,
    paidAmount: moneySchema,
    creditAmount: moneySchema,
    principalOpen: moneySchema,
    overdueDays: z.number().int().min(0),
    feeAmount: moneySchema,
    interestAmount: moneySchema,
});
export const portalDunningDetailSchema = portalDunningSummarySchema.extend({
    items: z.array(portalDunningItemSchema),
});
export const portalDunningListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const portalDunningListResponseSchema = z.object({
    data: z.array(portalDunningSummarySchema),
    meta: paginationMetaSchema,
});
export const portalDashboardSchema = z.object({
    documentCounts: z.record(portalDocumentTypeSchema, z.number().int().min(0)),
    openInvoices: z.object({
        count: z.number().int().min(0),
        totals: z.array(z.object({ currency: z.string().length(3), total: moneySchema })),
    }),
    recentDocuments: z.array(portalDocumentSummarySchema),
    recentDunningNotices: z.array(portalDunningSummarySchema),
});
export const portalCatalogQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(24),
    q: z.string().trim().min(1).max(120).optional(),
    categoryId: idSchema.optional(),
    productGroup: z.string().trim().min(1).max(120).optional(),
    favoritesOnly: z
        .preprocess((value) => value === 'true' || value === true, z.boolean())
        .default(false),
    lastOrderedOnly: z
        .preprocess((value) => value === 'true' || value === true, z.boolean())
        .default(false),
});
export const portalCatalogPackagingSchema = z.object({
    id: idSchema,
    code: z.string(),
    name: z.string(),
    unitCode: z.string(),
    factor: quantitySchema,
    minOrderQuantity: quantitySchema.nullable(),
    orderStepQuantity: quantitySchema.nullable(),
});
export const portalAvailabilityStatusSchema = z.enum([
    'AVAILABLE',
    'LOW',
    'OUT_OF_STOCK',
    'NOT_MANAGED',
    'ON_REQUEST',
]);
export const portalCatalogProductSchema = z.object({
    id: idSchema,
    articleNumber: z.string(),
    name: z.string(),
    customerDesignation: z.string().nullable(),
    description: z.string().nullable(),
    productGroup: z.string().nullable(),
    categories: z.array(z.object({ id: idSchema, name: z.string() })),
    image: z.object({ id: idSchema, altText: z.string() }).nullable(),
    baseUnit: z.string(),
    taxRatePct: z.string(),
    price: z.object({
        currency: z.string().length(3),
        unitPrice: moneySchema,
        discountPct: z.string(),
        effectivePrice: moneySchema,
        isCustomerSpecific: z.boolean(),
    }),
    packagings: z.array(portalCatalogPackagingSchema),
    availability: z.object({
        visibility: z.enum(['EXACT', 'STATUS']),
        status: portalAvailabilityStatusSchema,
        availableQuantity: quantitySchema.nullable(),
        leadTimeDays: z.number().int().min(0),
    }),
    favorite: z.boolean(),
    lastOrderedAt: isoDateTimeSchema.nullable(),
});
export const portalCatalogResponseSchema = z.object({
    data: z.array(portalCatalogProductSchema),
    meta: paginationMetaSchema,
});
export const portalFavoriteUpdateSchema = z.object({ favorite: z.boolean() }).strict();
export const portalCartItemWriteSchema = z
    .object({
    productId: idSchema,
    packagingId: idSchema.nullable().default(null),
    quantity: quantitySchema.refine((value) => Number(value) > 0, 'Die Menge muss größer als null sein.'),
})
    .strict();
export const portalCartItemSchema = z.object({
    id: idSchema,
    productId: idSchema,
    articleNumber: z.string(),
    name: z.string(),
    customerDesignation: z.string().nullable(),
    packagingId: idSchema.nullable(),
    packagingName: z.string().nullable(),
    quantity: quantitySchema,
    unitCode: z.string(),
    packagingFactor: quantitySchema,
    quantityBase: quantitySchema,
    currency: z.string().length(3),
    unitPrice: moneySchema,
    discountPct: z.string(),
    effectivePrice: moneySchema,
    lineNet: moneySchema,
    taxRatePct: z.string(),
    previousUnitPrice: moneySchema,
    previousCurrency: z.string().length(3),
    previousDiscountPct: z.string(),
    previousEffectivePrice: moneySchema,
    priceChanged: z.boolean(),
    availabilityStatus: portalAvailabilityStatusSchema,
    availableQuantity: quantitySchema.nullable(),
});
export const portalCartSchema = z.object({
    shippingAddressId: idSchema.nullable().optional(),
    addressSelectionVersion: z.number().int().optional(),
    id: idSchema,
    version: z.number().int().positive(),
    items: z.array(portalCartItemSchema),
    currency: z.string().length(3),
    discountPct: z.string(),
    netTotal: moneySchema,
    taxTotal: moneySchema,
    grossTotal: moneySchema,
    hasPriceChanges: z.boolean(),
    hasAvailabilityIssues: z.boolean(),
});
export const portalCheckoutPreviewInputSchema = z
    .object({
    cartVersion: z.number().int().positive(),
    shippingAddressId: idSchema.nullable().default(null),
    requestedDeliveryDate: isoDateSchema.nullable().default(null),
    customerPurchaseOrderNumber: z.string().trim().max(100).nullable().default(null),
    remark: z.string().trim().max(2_000).nullable().default(null),
    partialDeliveryAllowed: z.boolean(),
})
    .strict();
export const portalCheckoutPreviewSchema = z.object({
    cart: portalCartSchema,
    billingAddress: postalDocumentAddressSchema,
    shippingAddresses: z.array(z.object({ id: idSchema, label: z.string(), address: postalDocumentAddressSchema })),
    selectedShippingAddressId: idSchema.nullable(),
    paymentMethod: z.string(),
    paymentTermsDays: z.number().int().min(0),
    deliveryTerms: z.string().nullable(),
    partialDeliveryAllowed: z.boolean(),
    priceFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
});
export const portalCheckoutSubmitSchema = portalCheckoutPreviewInputSchema.extend({
    idempotencyKey: z.string().uuid(),
    priceFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
});
export const portalOrderResultSchema = z.object({
    documentId: idSchema,
    number: z.string(),
    status: documentStatusSchema,
    reviewStatus: z.enum(['PENDING', 'APPROVED', 'REJECTED']),
    submittedAt: isoDateTimeSchema,
    grossTotal: moneySchema,
    currency: z.string().length(3),
    idempotentReplay: z.boolean(),
});
export const portalOrderReviewSchema = z
    .object({
    action: z.enum(['APPROVE', 'REJECT']),
    reason: z.string().trim().max(1_000).nullable().default(null),
})
    .strict()
    .superRefine((value, context) => {
    if (value.action === 'REJECT' && (value.reason === null || value.reason.length < 3)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reason'],
            message: 'Eine Begründung ist erforderlich.',
        });
    }
});
//# sourceMappingURL=portal.js.map